/** The labyrinth: walls INSIDE a zone, not just between zones.
 *
 * `raiseRidges` walls the seams BETWEEN zones and leaves one or two gaps in
 * each. That is what makes a zone a region with a door instead of a colour
 * change, and it is the whole of the map's structure: inside a zone the ground
 * is open field, so a hero crosses it in a straight line and the only decision
 * on the way is which of the two doors to leave by. A map made of six open
 * fields is a map with six decisions in it.
 *
 * This pass applies the same idea one level down. Each land zone is partitioned
 * into CHAMBERS - by a multi-source flood from seeds spread across the zone, so
 * the chambers follow the ground rather than a grid - and the seam between two
 * chambers becomes a wall with one or two gaps in it, exactly as a zone seam
 * does. The result is a region a hero has to thread rather than cross: chambers
 * of open ground joined by chokepoints, which is what "maze" means on an
 * adventure map and what a classic map gets from its mountain ranges.
 *
 * Two things make this safe to do at all:
 *
 *   * Every seam keeps a gap BY CONSTRUCTION, so the partition never seals a
 *     chamber. The gaps are chosen before any cell is walled, from the seam's
 *     own cells, and the wall skips everything within one step of one.
 *   * The walls are raised as ROCK TERRAIN, before `repairConnectivity` runs,
 *     so the generator's existing guarantee covers them: whatever the flood and
 *     the water and the pre-existing ridges do between them, the land comes out
 *     as one walkable piece. This matters more than it sounds - the reachability
 *     audit does not open walls, it DELETES whatever it cannot reach, so a pass
 *     that sealed a pocket would quietly bin the mines inside it.
 *
 * A wall's MATERIAL is decided here and applied much later. Every wall is rock
 * to begin with, because rock is what the connectivity repair understands; the
 * ones marked `wooded` are handed to `plantWoodWalls` at dressing time, which
 * puts the ground back and grows a belt of forest on it instead. Converting a
 * wall from rock to trees can only ever ADD a way through - if a tree cannot be
 * planted the rock stays - so the guarantee above survives the change of look.
 */

import type { AdventureMapState, TerrainKind } from "@game/core";
import { setTerrain } from "@game/core";
import type { Grid } from "./grid.js";
import { orthoNeighbours, terrainOf, xOf, yOf } from "./grid.js";
import type { Zone } from "./biomes.js";
import { type Rng, int, shuffle } from "./rng.js";

export interface LabyrinthResult {
  /** Cells left open through a chamber wall; each is a place worth guarding. */
  readonly gates: readonly number[];
  /** Wall cells that are to become forest rather than mountain. */
  readonly wooded: readonly number[];
  /**
   * One cell in the middle of every chamber, for the caller to add to the
   * connectivity repair's `mustConnect`.
   *
   * The spanning tree makes every chamber reachable in THEORY, and theory is
   * not quite enough: seams meet at their ends, three chambers can share a
   * corner, and a doorway punched through a two-row wall does not always punch
   * through the three rows a T-junction makes of it. Measured, that left 1.9%
   * of the walkable land cut off at full strength - and the reachability audit
   * answers ground it cannot reach by DELETING whatever stands on it.
   *
   * So the pass proposes and `repairConnectivity` disposes: it already knows
   * how to reopen the cheapest line of rock until everything named here is one
   * piece, and naming the chambers costs one flood it was going to run anyway.
   */
  readonly chambers: readonly number[];
}

export const NO_LABYRINTH: LabyrinthResult =
  Object.freeze({ gates: [], wooded: [], chambers: [] });

/**
 * How much ground one chamber holds at FULL strength: chamber count is the
 * zone's area over this, scaled by `mazeBp`, so a big zone is cut into more
 * pieces rather than into bigger ones - it is a big open zone that reads as a
 * field, and cutting it into four still leaves four fields.
 *
 * 55 and not the 130 this started at. Zones on a 72x72 map run 350-600 cells,
 * and at 130 even full strength gave them four chambers each: the walls landed
 * but the route between the capitals did not lengthen at all (tortuosity 1.47
 * open, 1.42 walled - it went the wrong way). Four rooms in a region is still a
 * region a hero crosses.
 */
const CELLS_PER_CHAMBER = 55;
/** A zone smaller than this is already a chamber; cutting it makes a corridor. */
const MIN_ZONE_CELLS = 150;
/** Fewer border cells than this is a corner, not a wall worth building. */
const MIN_SEAM_CELLS = 5;
/**
 * A seam at least this long is worth a second way through. Long, because the
 * second door is what a maze is paid for: two doors in a fifteen-cell wall is a
 * wall a hero walks past, and the detour it costs is a step and a half.
 */
const TWO_GATE_SEAM = 26;
/** Share of walls grown as forest rather than raised as rock, in basis points. */
const WOODED_BP = 4200;

const UNASSIGNED = -1;

/**
 * Wall the chambers of every land zone. `mazeBp` is 0 (leave the zones open,
 * which is what this generator did before) to 10000 (as many chambers as the
 * ground will carry).
 */
export function carveLabyrinth(
  m: AdventureMapState, r: Rng, zones: readonly Zone[], zoneOf: Int32Array,
  protectedCells: ReadonlySet<number>, mazeBp: number,
): LabyrinthResult {
  if (mazeBp <= 0) return NO_LABYRINTH;
  const g: Grid = { width: m.width, height: m.height };
  const gates: number[] = [];
  const wooded: number[] = [];
  const chambers: number[] = [];
  // One buffer for the whole run: `chamberOf` is only ever read for cells of
  // the zone being worked on, and every zone overwrites its own cells first.
  const chamberOf = new Int32Array(m.width * m.height).fill(UNASSIGNED);

  for (const z of zones) {
    if (z.terrain === "water") continue;
    const open = z.cells.filter((i) => open4(m, i));
    if (open.length < MIN_ZONE_CELLS) continue;
    const want = chamberCount(open.length, mazeBp);
    if (want < 2) continue;
    const seeds = spreadSeeds(g, r, open, want);
    if (seeds.length < 2) continue;
    // Membership as a set, not a search over `open`: `Zone.cells` is grown by a
    // flood and is in flood order, not index order, so a binary search over it
    // answers wrongly. The set is also what keeps one zone's chambers out of
    // the next one's - `chamberOf` is one buffer for the whole run and still
    // holds the previous zone's ids.
    const mine = new Set(open);
    partition(g, m, mine, seeds, chamberOf);
    chambers.push(...seeds);
    wallSeams(m, g, r, open, mine, chamberOf, seeds.length, protectedCells, mazeBp,
              gates, wooded);
  }
  return { gates, wooded, chambers };
}

/**
 * How many chambers this much ground is cut into at this strength.
 *
 * Rounds to zero rather than up to two below one chamber's worth: `mazeBp` is a
 * dial and it has to be able to mean "barely". Rounding up made 2000 and 4500
 * produce byte-identical maps, because both landed on the floor.
 */
function chamberCount(cells: number, mazeBp: number): number {
  const k = Math.round((cells * mazeBp) / (CELLS_PER_CHAMBER * 10000));
  return k < 2 ? 0 : Math.min(k, 24);
}

/** Ground a wall may be raised on, and a chamber may be grown across. */
function open4(m: AdventureMapState, i: number): boolean {
  const t = terrainOf(m, i);
  return t !== "water" && t !== "rock";
}

/**
 * `want` cells of `open`, spread out.
 *
 * Farthest-point sampling rather than a random draw: k random cells in a zone
 * clump, and two chamber seeds three steps apart give a seam five cells long
 * that is thrown away by `MIN_SEAM_CELLS` - so a random draw quietly produces
 * far fewer walls than it asks for. Euclidean distance is enough here; the
 * partition itself is a flood and follows the real ground.
 */
function spreadSeeds(g: Grid, r: Rng, open: readonly number[], want: number): number[] {
  const first = open[int(r, open.length)]!;
  const seeds = [first];
  const best = new Float64Array(open.length);
  for (let k = 0; k < open.length; k++) best[k] = sq(g, open[k]!, first);
  while (seeds.length < want) {
    let at = -1;
    let far = -1;
    for (let k = 0; k < open.length; k++) {
      if (best[k]! > far) { far = best[k]!; at = k; }
    }
    if (at < 0 || far <= 0) break;
    const chosen = open[at]!;
    seeds.push(chosen);
    for (let k = 0; k < open.length; k++) {
      const d = sq(g, open[k]!, chosen);
      if (d < best[k]!) best[k] = d;
    }
  }
  return seeds;
}

const sq = (g: Grid, a: number, b: number): number => {
  const dx = xOf(g, a) - xOf(g, b), dy = yOf(g, a) - yOf(g, b);
  return dx * dx + dy * dy;
};

/** Multi-source breadth-first flood: every open cell joins its nearest seed. */
function partition(
  g: Grid, m: AdventureMapState, mine: ReadonlySet<number>, seeds: readonly number[],
  chamberOf: Int32Array,
): void {
  for (const i of mine) chamberOf[i] = UNASSIGNED;
  const queue: number[] = [];
  seeds.forEach((cell, c) => { chamberOf[cell] = c; queue.push(cell); });
  const buf: number[] = [];
  for (let head = 0; head < queue.length; head++) {
    const cur = queue[head]!;
    const c = chamberOf[cur]!;
    for (const nb of orthoNeighbours(g, cur, buf)) {
      if (!mine.has(nb) || chamberOf[nb] !== UNASSIGNED || !open4(m, nb)) continue;
      chamberOf[nb] = c;
      queue.push(nb);
    }
  }
}

/**
 * Wall the chamber seams, leaving the doors that make a MAZE rather than a
 * colonnade.
 *
 * This is the part that decides whether any of it is worth doing. A door in
 * every seam sounds like the cautious choice and is in fact the useless one: a
 * flood partition into k chambers has about 3k seams, so a door in each leaves
 * a room graph of average degree six, and a room graph of degree six is an open
 * field with some pillars in it. Measured: chamber walls with a door apiece
 * doubled the wall sprites on the map and moved the walk between the two
 * capitals by nothing at all - 1.47 open, 1.46 walled.
 *
 * What makes a maze is that the doors form a SPANNING TREE of the chambers.
 * Then there is exactly one route between any two rooms, a wrong turn is a
 * wrong turn, and the ground behind a wall is somewhere you have to go round to
 * reach. So: union-find over the chambers, seams considered in random order,
 * and a seam gets a door if and only if it joins two chambers not already
 * joined. Every other seam is walled shut - or, `mazeBp` short of full
 * strength, opened anyway with the leftover probability, which is the dial
 * between "one route" and "several".
 *
 * Connectivity is still guaranteed by construction and by the same argument as
 * before: a spanning tree touches every chamber, and the doors are chosen
 * before any cell is walled. Seams too short to be worth walling are unioned
 * first, because they stay open whatever the tree says and the tree must not
 * spend a door on a pair that is already joined by one.
 */
function wallSeams(
  m: AdventureMapState, g: Grid, r: Rng, open: readonly number[],
  mine: ReadonlySet<number>, chamberOf: Int32Array, chambers: number,
  protectedCells: ReadonlySet<number>, mazeBp: number,
  gates: number[], wooded: number[],
): void {
  const seams = collectSeams(g, open, mine, chamberOf);

  const parent = new Int32Array(chambers);
  for (let c = 0; c < chambers; c++) parent[c] = c;
  const find = (c: number): number => {
    let root = c;
    while (parent[root] !== root) root = parent[root]!;
    for (let at = c; parent[at] !== root;) { const next = parent[at]!; parent[at] = root; at = next; }
    return root;
  };
  const union = (a: number, b: number): boolean => {
    const ra = find(a), rb = find(b);
    if (ra === rb) return false;
    parent[ra] = rb;
    return true;
  };

  // A seam too short to wall is a doorway already; it joins its two chambers
  // whatever else happens, so it is unioned before the tree is grown.
  const walls: Seam[] = [];
  for (const seam of seams.values()) {
    if (seam.cells.length < MIN_SEAM_CELLS) union(seam.a, seam.b);
    else walls.push(seam);
  }
  shuffle(r, walls);

  // Two phases, and the split is the whole safety argument. Every door in the
  // ZONE is chosen before any cell in it is walled, because a door is only a
  // door if nothing walls it afterwards - and seams overlap at their ends, so a
  // seam walled later would otherwise close a door an earlier seam had spent
  // its one tree edge on and seal the chamber behind it. That is not a
  // hypothetical: doing it seam by seam left 1.9% of the walkable land cut off
  // from the capitals at full strength, and the reachability audit answers
  // land it cannot reach by deleting whatever was standing on it.
  const plan: { readonly seam: Seam; readonly doors: readonly number[] }[] = [];
  const doorway = new Set<number>();
  for (const seam of walls) {
    const onTree = union(seam.a, seam.b);
    const spared = !onTree && int(r, 10000) >= mazeBp;
    const doors = onTree || spared ? pickDoors(r, seam.cells) : [];
    plan.push({ seam, doors });
    gates.push(...doors);
    // A doorway is the door cell and its four orthogonal neighbours - a plus,
    // not a three-by-three block. The seam is two rows, so the plus is exactly
    // wide enough to punch through it and no wider; a block clears a
    // three-cell-wide breach, which a hero walks through without noticing that
    // anything was ever in the way. Where a plus is not enough - a corner where
    // three chambers meet - `repairConnectivity` opens the rest, which is why
    // `chambers` is returned.
    for (const d of doors) {
      doorway.add(d);
      for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
        const nx = xOf(g, d) + dx, ny = yOf(g, d) + dy;
        if (nx < 0 || ny < 0 || nx >= g.width || ny >= g.height) continue;
        doorway.add(ny * g.width + nx);
      }
    }
  }

  for (const { seam } of plan) {
    const asWood = int(r, 10000) < WOODED_BP;
    for (const i of [...seam.cells, ...seam.facing]) {
      if (protectedCells.has(i) || doorway.has(i)) continue;
      if (terrainOf(m, i) === "rock") continue;
      setTerrain(m, xOf(g, i), yOf(g, i), 0, "rock");
      if (asWood) wooded.push(i);
    }
  }
}

interface Seam {
  readonly a: number;
  readonly b: number;
  /** Cells on the lower chamber's side. Doors are chosen out of these. */
  readonly cells: number[];
  /** The cells facing them across the seam. Walled too; see `collectSeams`. */
  readonly facing: Set<number>;
}

/**
 * Every chamber-to-chamber border in this zone: the cells on the lower side,
 * AND the cells facing them across it.
 *
 * Both sides, because a hero moves diagonally and a one-sided wall does not
 * stop him. A flood partition's borders are mostly diagonal, so the cells on
 * one side of one form a staircase, and a staircase of blocked cells has a
 * diagonal gap at every step. Walling one side tripled the wall sprites on the
 * map, took eight percent of the ground out of play, and moved the walk between
 * the capitals by nothing - 1.47 open, 1.46 walled - because every wall on the
 * map had a hole in it every second cell.
 *
 * Two cells thick is also what a range should be: a one-cell ridge dressed with
 * mountains reads as a hedge of rock seen edge-on, and the second row gives the
 * renderer's ground-line sort something to sort.
 */
function collectSeams(
  g: Grid, open: readonly number[], mine: ReadonlySet<number>, chamberOf: Int32Array,
): Map<string, Seam> {
  const seams = new Map<string, Seam>();
  const buf: number[] = [];
  for (const i of open) {
    const a = chamberOf[i]!;
    if (a === UNASSIGNED) continue;
    for (const nb of orthoNeighbours(g, i, buf)) {
      if (!mine.has(nb)) continue;
      const b = chamberOf[nb]!;
      if (b === UNASSIGNED || b <= a) continue;
      const key = `${String(a)}:${String(b)}`;
      let seam = seams.get(key);
      if (seam === undefined) {
        seam = { a, b, cells: [], facing: new Set<number>() };
        seams.set(key, seam);
      }
      if (seam.cells[seam.cells.length - 1] !== i) seam.cells.push(i);
      seam.facing.add(nb);
    }
  }
  return seams;
}

/**
 * Where a seam is left open. One door, or two on a long one.
 *
 * Sorted by cell index first, which for a seam is a consistent order along it -
 * the cells arrive in the zone flood's order, which is order of distance from
 * the zone seed and says nothing about position on this seam, so picking "the
 * one a third of the way along" out of that list picks an arbitrary cell and
 * two doors can land side by side.
 */
function pickDoors(r: Rng, cells: readonly number[]): number[] {
  const along = [...cells].sort((a, b) => a - b);
  const doors = along.length >= TWO_GATE_SEAM ? 2 : 1;
  const out: number[] = [];
  for (let k = 0; k < doors; k++) {
    const lo = Math.trunc((k * along.length) / doors);
    const hi = Math.trunc(((k + 1) * along.length) / doors);
    out.push(along[lo + int(r, Math.max(1, hi - lo))]!);
  }
  return out;
}

/**
 * What ground a wooded wall stands on: the terrain most of its neighbours have,
 * and only when that terrain may legally touch every one of them.
 *
 * `smoothSeams` has already run by the time a wall is planted, so putting the
 * wrong ground back would leave a seam the biome rules forbid - snow against
 * swamp - with nothing left in the pipeline to bridge it. Where no neighbour
 * terrain is safe the caller leaves the rock alone and the wall wears mountains
 * instead, which is a look and not a defect.
 */
export function groundUnder(
  m: AdventureMapState, g: Grid, cell: number,
  plausible: (a: TerrainKind, b: TerrainKind) => boolean,
): TerrainKind | null {
  const buf: number[] = [];
  const around: TerrainKind[] = [];
  for (const nb of orthoNeighbours(g, cell, buf)) {
    const t = terrainOf(m, nb);
    if (t === "rock" || t === "water") continue;
    around.push(t);
  }
  if (around.length === 0) return null;
  const count = new Map<TerrainKind, number>();
  for (const t of around) count.set(t, (count.get(t) ?? 0) + 1);
  let best: TerrainKind | null = null;
  let most = 0;
  for (const [t, n] of count) if (n > most) { most = n; best = t; }
  if (best === null) return null;
  for (const t of around) if (t !== best && !plausible(best, t)) return null;
  return best;
}
